import { useState } from "react";
import { motion } from "framer-motion";
import AnimatedSection from "@/components/common/AnimatedSection";
import LightboxModal from "@/components/common/LightboxModal";
import PageHero from "@/components/common/PageHero";
import PageTransition from "@/components/layout/PageTransition";
import { pageHeroImages } from "@/config/site";
import drabhishek from "../assets/drabhishek.jpg";
import drnamita from "../assets/drnamita.jpg";
import f from "../assets/4.jpg";
import s from "../assets/5.jpg";
import t from "../assets/6.jpg";

const doctor = {
  name: "Dr. Abhishek Somnath Kavlekar",
  title: "MDS Prosthodontist & Implantologist",
  image: drabhishek,

  about:
    "Dr. Abhishek Kavlekar is a leading implantologist and prosthodontist with over a decade of experience in advanced dental rehabilitation. Known for precision-driven treatments and patient-first care, he has contributed extensively to implant dentistry through clinical excellence, teaching, and global lectures.",

  highlights: [
    "Co-Founder & Chief Implantologist – Kavlekar Dental (2015–Present)",
    "Chief Implant Consultant to 20+ Clinics",
    "Assistant Professor – SDM Dental College (2021–2023)",
  ],

  education: [
    "MDS Prosthodontics – SDM Dental College (2012–2015)",
    "BDS – SDM Dental College (2005–2010)",
  ],

  certifications: [
    "Implant Complication Management – 2021",
    "Mastering Soft Tissue – 2022",
    "Full Mouth Rehabilitation & TMD – 2023",
  ],

  awards: [
    "Young ITI World Series – Audience Favourite (Singapore, 2024)",
    "Young Achievers Award – Lions Club, Hubli",
    "Multiple National Best Paper & Poster Awards",
  ],

  affiliations: [
    "International Team for Implantology (ITI)",
    "Indian Society of Oral Implantology",
    "Indian Prosthodontic Society",
    "Indian Dental Association – Hubli",
  ],
};

const gallery = [
  {
    title: "Comfortable Patient Area",
    image: f
  },
  {
    title: "Comfortable Patient Area",
    image: s
  },
  {
    title: "Advance Dental Setup",
    image: t
  },
];

export default function AboutPage() {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <PageTransition testId="about-page">
      <div className={selectedImage ? "blur-[1px]" : ""}>
        <PageHero
          title="About Kavlekar Dental"
          subtitle="High standards in sterilization, precision treatment, and deeply personalized patient care."
          image={pageHeroImages.about}
          testId="about-page-hero"
        />

        <div className="mx-auto max-w-7xl space-y-20 px-6 py-16 md:px-10 md:py-24">

        <AnimatedSection>
  <div className="w-full">
    <h2 className="font-[Playfair_Display] text-4xl md:text-5xl text-[#1a1a1a]">
      About Kavlekar Dental
    </h2>

    <p className="mt-6 text-[#555] w-full leading-relaxed text-lg">
      Kavlekar Dental is built on precision, trust, and advanced clinical care.
      We specialize in implant dentistry, full-mouth rehabilitation, and
      modern digital treatments — all delivered in a calm, patient-first
      environment.
    </p>

    <p className="mt-4 text-[#555] leading-relaxed text-lg">
      Our approach combines cutting-edge technology with years of expertise to
      ensure long-term oral health, comfort, and confidence for every patient.
    </p>
  </div>
</AnimatedSection>

          <AnimatedSection>
  <div className="grid md:grid-cols-2 gap-12 items-start mt-16">

    {/* LEFT IMAGE */}
    <div className="sticky top-24">
      <div className="overflow-hidden rounded-3xl border border-[#f2e6c9]">
        <img
          src={doctor.image}
          alt={doctor.name}
          className="w-full h-[500px] object-cover"
        />
      </div>

      {/* Name + Title */}
      <div className="mt-6">
        <h3 className="font-[Playfair_Display] text-3xl text-[#1a1a1a]">
          {doctor.name}
        </h3>
        <p className="mt-2 text-sm uppercase tracking-widest text-[#8b7a55]">
          {doctor.title}
        </p>
      </div>
    </div>

    {/* RIGHT CONTENT */}
    <div className="space-y-10">

      {/* ABOUT */}
      <div>
        <h4 className="text-xl font-semibold text-[#1a1a1a] mb-3">
          Profile
        </h4>
        <p className="text-[#555] leading-relaxed">
          {doctor.about}
        </p>
      </div>

      {/* HIGHLIGHTS */}
      <div>
        <h4 className="text-xl font-semibold text-[#1a1a1a] mb-3">
          Key Highlights
        </h4>
        <ul className="space-y-2 text-[#555]">
          {doctor.highlights.map((item, i) => (
            <li key={i}>• {item}</li>
          ))}
        </ul>
      </div>

      {/* EDUCATION */}
      <div>
        <h4 className="text-xl font-semibold text-[#1a1a1a] mb-3">
          Education
        </h4>
        <ul className="space-y-2 text-[#555]">
          {doctor.education.map((item, i) => (
            <li key={i}>• {item}</li>
          ))}
        </ul>
      </div>

      {/* CERTIFICATIONS */}
      <div>
        <h4 className="text-xl font-semibold text-[#1a1a1a] mb-3">
          Certifications
        </h4>
        <ul className="space-y-2 text-[#555]">
          {doctor.certifications.map((item, i) => (
            <li key={i}>• {item}</li>
          ))}
        </ul>
      </div>

      {/* AWARDS */}
      <div>
        <h4 className="text-xl font-semibold text-[#1a1a1a] mb-3">
          Awards & Recognition
        </h4>
        <ul className="space-y-2 text-[#555]">
          {doctor.awards.map((item, i) => (
            <li key={i}>🏆 {item}</li>
          ))}
        </ul>
      </div>

      {/* AFFILIATIONS */}
      <div>
        <h4 className="text-xl font-semibold text-[#1a1a1a] mb-3">
          Professional Affiliations
        </h4>
        <ul className="space-y-2 text-[#555]">
          {doctor.affiliations.map((item, i) => (
            <li key={i}>• {item}</li>
          ))}
        </ul>
      </div>

    </div>
  </div>
</AnimatedSection>

          <AnimatedSection
            className="grid gap-8 rounded-2xl border border-[#f2e6c9] bg-[#fffdf8] p-7 md:grid-cols-2 md:p-10"
            testId="about-philosophy-section"
          >
            <div>
              <h3
                className="font-[Playfair_Display] text-3xl text-[#1a1a1a]"
                data-testid="about-philosophy-title"
              >
                Our Philosophy
              </h3>
              <p
                className="mt-4 font-[Manrope] text-sm leading-relaxed text-[#555555]"
                data-testid="about-philosophy-text"
              >
                We combine clinical excellence with calm, transparent communication.
                Every treatment plan is built around hygiene, safety, and long-term
                patient confidence.
              </p>
            </div>
            <div>
              <h3
                className="font-[Playfair_Display] text-3xl text-[#1a1a1a]"
                data-testid="about-technology-title"
              >
                Our Technology
              </h3>
              <p
                className="mt-4 font-[Manrope] text-sm leading-relaxed text-[#555555]"
                data-testid="about-technology-text"
              >
                Digital X-rays, digital impressions, and microscope-assisted
                procedures support precise diagnostics and comfortable treatment.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection testId="about-gallery-section">
            <h3
              className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-4xl"
              data-testid="about-gallery-title"
            >
              Facility & Environment
            </h3>
            <div className="mt-7 grid gap-5 md:grid-cols-3">
              {gallery.map((item) => (
                <button
                  key={item.title}
                  onClick={() => setSelectedImage(item)}
                  className="group overflow-hidden rounded-2xl border border-[#f2e6c9] bg-white text-left"
                  data-testid={`about-gallery-button-${item.title
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      data-testid={`about-gallery-image-${item.title
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    />
                  </div>
                  <p
                    className="px-4 py-3 font-[Manrope] text-sm text-[#333333]"
                    data-testid={`about-gallery-caption-${item.title
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    {item.title}
                  </p>
                </button>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </div>

      <LightboxModal
        isOpen={Boolean(selectedImage)}
        image={selectedImage?.image}
        title={selectedImage?.title}
        onClose={() => setSelectedImage(null)}
      />
    </PageTransition>
  );
}
